import clsx from 'clsx';
import * as React from 'react';

import { table } from '@gitbook/doc-core';
import type { EditorContainer } from '@gitbook/slate-react';
import { Tooltip } from '@gitbook/spine-react';

import { useDroppableWithCallback } from '../../../../DragAndDrop';
import { TABLE_BORDER_WIDTH, TABLE_ROW_SETTINGS_WIDTH } from '../../hooks/useTableLayout';
import { useColumnResizer } from './utils/useColumnResizer';

interface ColumnResizeHandleProps {
    columnId: string;
    tableKey: string;
    editor: EditorContainer;
    initialWidth?: number;
    columnOrder: string[];
}

/**
 * Resizable column handle component that can be attached to the end of table columns
 * to allow for resizing. Uses the useColumnResizer hook for resize logic.
 */
export function ColumnResizeHandle({
    columnId,
    tableKey,
    editor,
    initialWidth,
    columnOrder,
}: ColumnResizeHandleProps): React.ReactElement {
    const {
        isDragging,
        handlePointerDown,
        handlePointerMove,
        handlePointerUp,
        cumulativeLeftPosition,
        currentWidth,
    } = useColumnResizer({
        columnId,
        tableKey,
        editor,
        initialWidth,
        columnOrder,
    });

    /* Allow dropping a column on the resizer to position in place */
    const drop = useDroppableWithCallback({
        shouldAccept: React.useCallback(
            (item) =>
                item.type === 'table-definition' &&
                columnId !== item.id &&
                tableKey === item.blockKey,
            [columnId, tableKey]
        ),
        onDrop: React.useCallback(
            (item) => {
                if (item.type === 'table-definition') {
                    editor.change((change) => {
                        table.moveColumn({
                            change,
                            tableKey,
                            columnId: item.id,
                            beforeColumnId: columnId,
                        });
                    });
                }
            },
            [editor, tableKey, columnId]
        ),
    });

    return (
        <div
            className="absolute top-2 bottom-0 z-10 flex w-[3px] items-center opacity-100 group-hover/tablecolumn:opacity-100"
            style={{
                left: cumulativeLeftPosition - TABLE_BORDER_WIDTH / 2 + TABLE_ROW_SETTINGS_WIDTH,
            }}
        >
            <Tooltip label={`${Math.round(currentWidth)}px`} position="top" isOpen={isDragging}>
                <div
                    ref={drop.setNodeRef}
                    className={clsx(
                        'absolute top-0 bottom-0 left-0 w-[3px] cursor-col-resize transition-colors',
                        'hover:bg-[var(--sp-color-primary-300)]',
                        'active:bg-[var(--sp-color-primary-400)]',
                        drop.isOver && 'bg-primary',
                        isDragging && 'bg-[var(--sp-color-primary-400)]'
                    )}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                />
            </Tooltip>
        </div>
    );
}
